import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as XLSX from 'xlsx';
import { ChevronDown, Check, CircleCheck, Funnel, Link2, Smartphone, Trash2, TriangleAlert } from 'lucide-react';
import { IconCalendarSearch, IconCategoryPlus, IconCertificate, IconShieldSearch } from '@tabler/icons-react';
import '@fontsource-variable/geist/wght.css';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { BoardToolbarExpandSearch } from '../../board/toolbar/BoardToolbarExpandSearch';
import PermColumnFilterPopover from '../../users-customers/components/PermColumnFilterPopover';
import { GoDevicesDisplayMenu } from './GoDevicesDisplayMenu';
import ProcessConfigSkeleton from '../../process-config/ConfigStatusView2Skeleton';
import '../../process-config/ConfigStatusView2.css';
import '../../board/toolbar/BoardClickUpFilterChip.css';
import '../../board/toolbar/BoardToolbarSearchFilters.css';
import '../../board/board-search-filters/BoardSearchFilters.css';
import {
  RangeCalendarSingle,
  formatPickerDate,
  toYmd,
} from '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker';
import '../../equipment-consult/components/EquipmentConsultProductionPeriodPicker.css';
import './GoDispositivosConfig.css';
import { GoHoverTooltip } from './GoHoverTooltip';
import {
  GoDeviceInstallStatusChip,
  GoDeviceInstallStatusModal,
} from './GoDeviceInstallStatus';

const API_BASE_URL = getApiBaseUrl();

const GO_PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.vervify.mobile';
/* ecc=L + URL curta = módulos maiores / QR mais legível no card compacto */
const GO_PLAY_STORE_QR_SRC = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&ecc=L&margin=2&qzone=2&data=${encodeURIComponent(GO_PLAY_STORE_URL)}`;

/** Compara versões tipo 1.2.3 (partes numéricas). */
function compareAppVersion(a, b) {
  const pa = String(a || '')
    .trim()
    .split(/[.+-]/)
    .map((x) => {
      const n = parseInt(x, 10);
      return Number.isFinite(n) ? n : 0;
    });
  const pb = String(b || '')
    .trim()
    .split(/[.+-]/)
    .map((x) => {
      const n = parseInt(x, 10);
      return Number.isFinite(n) ? n : 0;
    });
  const len = Math.max(pa.length, pb.length);
  for (let i = 0; i < len; i += 1) {
    const da = pa[i] || 0;
    const db = pb[i] || 0;
    if (da !== db) return da - db;
  }
  return 0;
}

/**
 * null = sem referência / sem versão no dispositivo; true = atualizado; false = desatualizado.
 */
function isDeviceAppUpToDate(device, publishedApp) {
  if (!publishedApp?.appVersion) return null;
  const deviceVersion = String(device?.appVersion || '').trim();
  if (!deviceVersion) return null;
  const vc = compareAppVersion(deviceVersion, publishedApp.appVersion);
  if (vc > 0) return true;
  if (vc < 0) return false;
  const pubBuild = String(publishedApp.appBuild || '').trim();
  if (!pubBuild) return true;
  const deviceBuild = String(device?.appBuild || '').trim();
  if (!deviceBuild) return false;
  const db = Number(deviceBuild);
  const pb = Number(pubBuild);
  if (Number.isFinite(db) && Number.isFinite(pb)) return db >= pb;
  return deviceBuild === pubBuild;
}

function GooglePlayIcon({ size = 18, className = '' }) {
  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#EA4335"
        d="M3.6 2.1c-.4.2-.6.6-.6 1.1v17.6c0 .5.2.9.6 1.1l9.5-9.9L3.6 2.1z"
      />
      <path
        fill="#FBBC04"
        d="M16.1 14.7 13.1 11.7 3.6 21.9c.2.1.5.2.7.1l11.8-7.3z"
      />
      <path
        fill="#4285F4"
        d="M20.7 10.7 16.9 8.4l-3.8 3.3 3.8 3.3 3.8-2.3c.7-.4.7-1.5 0-2z"
      />
      <path
        fill="#34A853"
        d="M13.1 12.3 16.9 8.4 5.1 1.1C4.9 1 4.6 1 4.3 1.1l8.8 11.2z"
      />
    </svg>
  );
}

const DEVICE_COL_KEYS = [
  'nomeUsuario',
  'brand',
  'modelo',
  'osVersion',
  'appVersion',
  'deviceUid',
  'ativadoEm',
  'ultimoAcesso',
  'revogadoEm',
];
const DEVICE_COL_LABELS = {
  nomeUsuario: 'Usuário',
  brand: 'Marca',
  modelo: 'Modelo',
  osVersion: 'Versão OS',
  appVersion: 'Versão App',
  deviceUid: 'Device UID',
  ativadoEm: 'Ativação',
  ultimoAcesso: 'Últ. acesso',
  revogadoEm: 'Revogado',
};

const DEVICE_COL_TOGGLE_KEYS = [
  'nomeUsuario',
  'install',
  'brand',
  'modelo',
  'osVersion',
  'appVersion',
  'deviceUid',
  'ativadoEm',
  'ultimoAcesso',
  'revogadoEm',
];
const DEVICE_COL_TOGGLE_LABELS = { ...DEVICE_COL_LABELS, install: 'Instalação', deviceUid: 'UID' };

const DEV_STATUS_TABS = [
  { key: 'ativos', label: 'Ativos' },
  { key: 'todos', label: 'Todos' },
  { key: 'revogados', label: 'Revogados' },
  { key: 'duplicados', label: 'Duplicados' },
];

const DEV_COL_FILTER_KEYS = ['install', 'brand', 'modelo', 'osVersion', 'appVersion'];
const DEV_COL_FILTER_LABELS = {
  install: 'Instalação',
  brand: 'Marca',
  modelo: 'Modelo',
  osVersion: 'Versão OS',
  appVersion: 'Versão App',
};

function installStatusLabel(row) {
  if (isRevoked(row)) return 'Revogado';
  const st = row?.deviceState?.status;
  if (st === 'ok') return 'OK';
  if (st === 'attention') return 'Atenção';
  return 'Sem relatório';
}

function colFilterValue(row, key) {
  if (key === 'install') return installStatusLabel(row);
  const v = String(row?.[key] ?? '').trim();
  return v || '(Vazio)';
}

const DEV_GROUPS = [
  {
    key: 'ativos',
    label: 'Ativos',
    desc: 'Dispositivos com licença em uso',
    color: '#22c55e',
  },
  {
    key: 'revogados',
    label: 'Revogados',
    desc: 'Sem licença ativa no App Go',
    color: '#94a3b8',
  },
];

function initialsFromName(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

function isRevoked(row) {
  return row?.revogadoEm != null && String(row.revogadoEm).trim() !== '';
}

function addDaysYmd(days) {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + days);
  return toYmd(d);
}

function minRevokeYmd() {
  return addDaysYmd(2);
}

function maxRevokeYmd() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 5);
  return toYmd(d);
}

function startOfLocalDayMs(value) {
  const d = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(d.getTime())) return NaN;
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

function getRevokeUndoLock(revogadoEm) {
  if (revogadoEm == null || String(revogadoEm).trim() === '') {
    return { locked: false, remainingMs: 0, unlockAt: null };
  }
  const at = startOfLocalDayMs(revogadoEm);
  if (Number.isNaN(at)) return { locked: false, remainingMs: 0, unlockAt: null };
  const remainingMs = at - Date.now();
  return {
    locked: remainingMs > 0,
    remainingMs: Math.max(0, remainingMs),
    unlockAt: at,
  };
}

function formatRevokeLockTooltip(lock) {
  if (!lock?.locked) return 'Desfazer revogação';
  const when = lock.unlockAt ? formatDate(lock.unlockAt) : '';
  return when
    ? `Desfazer disponível a partir de ${when}`
    : 'Desfazer disponível a partir da data escolhida';
}

function normalizeUserName(name) {
  return String(name || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function formatDate(value) {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** Interpreta instante UTC (ISO com Z ou DATETIME MySQL sem offset) e formata em America/Sao_Paulo. */
function parseUtcInstant(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const s = String(value).trim();
  if (!s) return null;
  const hasOffset = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(s);
  const naive = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?(?:\.\d+)?$/);
  if (naive && !hasOffset) {
    const d = new Date(
      Date.UTC(
        Number(naive[1]),
        Number(naive[2]) - 1,
        Number(naive[3]),
        Number(naive[4]),
        Number(naive[5]),
        naive[6] != null ? Number(naive[6]) : 0
      )
    );
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatDateTime(value) {
  const d = parseUtcInstant(value);
  if (!d) return '—';
  try {
    const parts = new Intl.DateTimeFormat('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      hourCycle: 'h23',
    }).formatToParts(d);
    const get = (type) => parts.find((p) => p.type === type)?.value || '';
    const day = get('day');
    const month = get('month');
    const year = get('year');
    let hour = get('hour');
    const minute = get('minute');
    if (hour === '24') hour = '00';
    if (!day || !month || !year || hour === '' || minute === '') return '—';
    return `${day}/${month}/${year} ${hour}:${minute}`;
  } catch {
    const brt = new Date(d.getTime() - 3 * 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(brt.getUTCDate())}/${pad(brt.getUTCMonth() + 1)}/${brt.getUTCFullYear()} ${pad(brt.getUTCHours())}:${pad(brt.getUTCMinutes())}`;
  }
}

function fullColVisibility() {
  return DEVICE_COL_TOGGLE_KEYS.reduce((acc, k) => {
    acc[k] = true;
    return acc;
  }, {});
}

function GoDevAvatar({ name, photoUrl }) {
  const [failed, setFailed] = useState(false);
  const initials = initialsFromName(name);
  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt=""
        className="go-dev-avatar-img"
        referrerPolicy="no-referrer"
        decoding="async"
        onError={() => setFailed(true)}
      />
    );
  }
  return <div className="go-dev-avatar-fallback">{initials}</div>;
}

function GoDevToggle({ checked, onChange, disabled, ariaLabel, tooltip }) {
  const tip = tooltip || ariaLabel || '';
  return (
    <GoHoverTooltip text={tip} align="end">
      <button
        type="button"
        className={`go-dev-toggle${checked ? ' on' : ''}`}
        onClick={() => onChange(!checked)}
        disabled={disabled}
        aria-pressed={checked}
        aria-label={ariaLabel}
      >
        <span className="go-dev-toggle-knob" />
      </button>
    </GoHoverTooltip>
  );
}

/** Texto com reticências: tooltip branco só quando estiver truncado. */
function GoDevEllipsisText({ value, className = '', align = 'start' }) {
  const display = value != null && String(value).trim() !== '' ? String(value) : '—';
  const full = display === '—' ? '' : display;

  return (
    <GoHoverTooltip text={full} align={align} className="go-dev-ellipsis-wrap" onlyWhenTruncated>
      <span className={`go-dev-ellipsis ${className}`.trim()}>{display}</span>
    </GoHoverTooltip>
  );
}

function DeviceUidCell({ deviceUid }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const value = String(deviceUid || '').trim();

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  if (!value) {
    return <td className="go-dev-td go-dev-td-uid go-dev-td-center">—</td>;
  }

  const onCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  return (
    <td className="go-dev-td go-dev-td-uid go-dev-td-center">
      <GoHoverTooltip text={copied ? 'Copiado!' : value} align="center">
        <button
          type="button"
          className={`go-dev-uid-btn${copied ? ' is-copied' : ''}`}
          aria-label={copied ? 'Device UID copiado' : `Copiar Device UID ${value}`}
          onClick={onCopy}
        >
          {copied ? (
            <Check size={15} strokeWidth={2.25} aria-hidden />
          ) : (
            <IconShieldSearch size={16} stroke={1.75} aria-hidden />
          )}
        </button>
      </GoHoverTooltip>
    </td>
  );
}

function GoDevConfirmModal({ title, message, confirmLabel, onConfirm, onCancel, children, modalClassName = '' }) {
  return ReactDOM.createPortal(
    <div className="go-dev-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="go-dev-confirm-title">
      <div className={['go-dev-confirm-modal', modalClassName].filter(Boolean).join(' ')}>
        <h3 id="go-dev-confirm-title" className="go-dev-confirm-title">
          {title}
        </h3>
        {message ? <p className="go-dev-confirm-message">{message}</p> : null}
        {children}
        <div className="go-dev-confirm-actions">
          <button type="button" className="user-form-link-btn user-form-footer-btn" onClick={onCancel}>
            Cancelar
          </button>
          <button type="button" className="user-form-profile-save-btn user-form-footer-btn" onClick={onConfirm}>
            {confirmLabel || 'Confirmar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function Pcs2Section({
  icon: Icon,
  title,
  desc,
  children,
  tablerIcon = false,
  className = '',
  asideAction = null,
  asideMiddle = null,
}) {
  return (
    <section className={['pcs2-section', className].filter(Boolean).join(' ')}>
      <div
        className={`pcs2-section-aside${asideAction ? ' pcs2-section-aside--with-action' : ''}${asideMiddle ? ' go-dev-aside-with-middle' : ''}`}
      >
        <div className="pcs2-section-aside-main">
          <div className="pcs2-section-heading">
            {tablerIcon ? (
              <Icon size={16} stroke={1.75} aria-hidden />
            ) : (
              <Icon size={16} strokeWidth={1.75} aria-hidden />
            )}
            <h3 className="pcs2-section-title">{title}</h3>
          </div>
          {desc ? <p className="pcs2-section-desc">{desc}</p> : null}
        </div>
        {asideMiddle ? <div className="go-dev-aside-middle">{asideMiddle}</div> : null}
        {asideAction ? <div className="pcs2-section-aside-action">{asideAction}</div> : null}
      </div>
      <div className="pcs2-section-body">{children}</div>
    </section>
  );
}

function Pcs2Subblock({ title, desc, action, children }) {
  return (
    <div className="pcs2-subblock">
      {(title || desc || action) ? (
        <div className={`pcs2-subhead${action ? ' pcs2-subhead--with-action' : ''}`}>
          <div className="pcs2-subhead-copy">
            {title ? <h4 className="pcs2-subtitle">{title}</h4> : null}
            {desc ? <p className="pcs2-subdesc">{desc}</p> : null}
          </div>
          {action ? <div className="pcs2-subhead-action">{action}</div> : null}
        </div>
      ) : null}
      {children}
    </div>
  );
}

function goProductPlanLabel(idProduto) {
  const id = Number(idProduto);
  if (id === 3) return { key: 'basic', label: 'Basic' };
  if (id === 4) return { key: 'premium', label: 'Premium' };
  return null;
}

function LicenseUsagePanel({ used, max, percent, loading, error, idProduto }) {
  const pct = Math.min(100, Math.max(0, Number(percent) || 0));
  const plan = !loading ? goProductPlanLabel(idProduto) : null;
  return (
    <div className="go-dev-license-panel">
      <div className="go-dev-license-value">
        <span className="go-dev-license-value-text">
          {loading
            ? 'Carregando...'
            : `${Number(used || 0).toLocaleString('pt-BR')} / ${Number(max || 0).toLocaleString('pt-BR')} licenças`}
        </span>
        {plan ? (
          <span className={`go-dev-license-plan go-dev-license-plan--${plan.key}`} title={`Plano ${plan.label}`}>
            <IconCertificate size={13} stroke={1.75} aria-hidden />
            {plan.label}
          </span>
        ) : null}
      </div>
      <div className="go-dev-license-track" role="presentation">
        <div className="go-dev-license-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="go-dev-license-percent">
        {loading ? '' : `${Math.round(pct)}% utilizado`}
      </div>
      {error ? <p className="go-dev-license-error">{error}</p> : null}
    </div>
  );
}

function GoAppDownloadPanel({ publishedApp, loading }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const handleCopyLink = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(GO_PLAY_STORE_URL);
      setCopied(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  const versionLabel = publishedApp?.appVersion ? String(publishedApp.appVersion).trim() : '';
  const buildLabel = publishedApp?.appBuild ? String(publishedApp.appBuild).trim() : '';
  const hasRelease = Boolean(versionLabel || buildLabel);

  return (
    <div className="go-dev-app-download">
      <div className="go-dev-app-download-card">
        <div className="go-dev-app-download-qr-wrap">
          <img
            className="go-dev-app-download-qr"
            src={GO_PLAY_STORE_QR_SRC}
            alt="QR Code para baixar o Vervify Go na Google Play"
            width={80}
            height={80}
            loading="lazy"
            decoding="async"
          />
        </div>
        <div className="go-dev-app-download-copy">
          <div className="go-dev-app-download-title">
            <GooglePlayIcon size={18} />
            <span>Disponível na Google Play</span>
            <GoHoverTooltip text={copied ? 'Copiado!' : 'Copiar link da Play Store'} align="center">
              <button
                type="button"
                className={`go-dev-uid-btn go-dev-app-download-link-btn${copied ? ' is-copied' : ''}`}
                aria-label={copied ? 'Link copiado' : 'Copiar link da Google Play'}
                onClick={handleCopyLink}
              >
                {copied ? (
                  <Check size={15} strokeWidth={2.25} aria-hidden />
                ) : (
                  <Link2 size={15} strokeWidth={2} aria-hidden />
                )}
              </button>
            </GoHoverTooltip>
          </div>
          <p className="go-dev-app-download-desc">
            Aponte a câmera e abra o Vervify Go na GPlay
          </p>
          {!loading && hasRelease ? (
            <div className="go-dev-app-release" aria-label="Versão publicada do App Go">
              <span className="go-dev-app-release-label">Versão atual</span>
              <div className="go-dev-app-release-meta">
                {versionLabel ? (
                  <span className="go-dev-app-release-v">{versionLabel}</span>
                ) : null}
                {buildLabel ? (
                  <span className="go-dev-app-release-item">
                    <span className="go-dev-app-release-k">Build</span>
                    <span className="go-dev-app-release-v">{buildLabel}</span>
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

const DEV_SORT_ITEMS = DEVICE_COL_KEYS.map((k) => ({ value: k, label: DEVICE_COL_LABELS[k] }));
const DEV_COL_TOGGLE_ITEMS = DEVICE_COL_TOGGLE_KEYS.map((k) => ({ value: k, label: DEVICE_COL_TOGGLE_LABELS[k] }));

function GoDevicesToolbar({ searchQuery, onSearchChange, displayMenuProps }) {
  return (
    <div className="go-dev-toolbar">
      <BoardToolbarExpandSearch
        value={searchQuery}
        onChange={onSearchChange}
        placeholder="Pesquisar dispositivos..."
        ariaLabel="Pesquisar dispositivos"
      />
      <GoDevicesDisplayMenu
        sortItems={DEV_SORT_ITEMS}
        colItems={DEV_COL_TOGGLE_ITEMS}
        {...displayMenuProps}
      />
    </div>
  );
}

function GoDevStatusTabs({ value, onChange, counts }) {
  return (
    <div className="go-dev-status-tabs" role="tablist" aria-label="Filtrar dispositivos por status">
      {DEV_STATUS_TABS.map((tab) => {
        const active = value === tab.key;
        const n = counts[tab.key] || 0;
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={active}
            className={`go-dev-status-tab${active ? ' is-active' : ''}`}
            onClick={() => onChange(tab.key)}
          >
            {tab.key === 'duplicados' && n > 0 ? <span className="go-dev-status-tab-dot" aria-hidden /> : null}
            <span>{tab.label}</span>
            <span className="go-dev-status-tab-count">{n}</span>
          </button>
        );
      })}
    </div>
  );
}

function GoDevAttentionStrip({ outdatedCount, installAttentionCount, revokeBlockedCount }) {
  if (!outdatedCount && !installAttentionCount && !revokeBlockedCount) return null;
  const plural = (n) => `${n} aparelho${n !== 1 ? 's' : ''}`;
  return (
    <>
      {revokeBlockedCount ? (
        <div className="go-dev-attention-strip go-dev-attention-strip--danger" role="alert">
          <TriangleAlert size={14} strokeWidth={2.1} className="go-dev-attention-icon" aria-hidden />
          <span className="go-dev-attention-text">
            <span>
              <strong>{plural(revokeBlockedCount)}</strong> com fim de revogação travado por falta
              de licença disponível
            </span>
          </span>
        </div>
      ) : null}
      {outdatedCount || installAttentionCount ? (
        <div className="go-dev-attention-strip" role="status">
          <TriangleAlert size={14} strokeWidth={2.1} className="go-dev-attention-icon" aria-hidden />
          <span className="go-dev-attention-text">
            {outdatedCount ? (
              <span>
                <strong>{plural(outdatedCount)}</strong> com versão desatualizada do app
              </span>
            ) : null}
            {outdatedCount && installAttentionCount ? (
              <span className="go-dev-attention-sep">·</span>
            ) : null}
            {installAttentionCount ? (
              <span>
                <strong>{plural(installAttentionCount)}</strong> com instalação em atenção
              </span>
            ) : null}
          </span>
        </div>
      ) : null}
    </>
  );
}

function GoDevThFilter({ colKey, label, className, options, selected, onApply, openKey, setOpenKey }) {
  const anchorRef = useRef(null);
  const active = (selected || []).length > 0;
  return (
    <th className={className}>
      <div className="go-dev-th-with-filter">
        <span className="go-dev-th-label">{label}</span>
        <button
          ref={anchorRef}
          type="button"
          className={`go-dev-th-filter-btn${active ? ' is-active' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            setOpenKey((v) => (v === colKey ? '' : colKey));
          }}
          aria-label={`Filtrar ${label}`}
          aria-expanded={openKey === colKey}
        >
          <Funnel size={14} aria-hidden />
          {active ? <span className="go-dev-th-filter-dot" aria-hidden /> : null}
        </button>
      </div>
      <PermColumnFilterPopover
        anchorRef={anchorRef}
        isOpen={openKey === colKey}
        onClose={() => setOpenKey('')}
        options={options}
        selected={selected}
        onApply={onApply}
        listAriaLabel={`Filtrar ${label}`}
      />
    </th>
  );
}

function getSortValue(row, key) {
  switch (key) {
    case 'nomeUsuario':
    case 'brand':
    case 'modelo':
    case 'osVersion':
    case 'appVersion':
    case 'deviceUid':
      return String(row[key] || '').toLowerCase();
    case 'ativadoEm':
    case 'ultimoAcesso':
    case 'revogadoEm':
      return row[key] ? new Date(row[key]).getTime() : 0;
    default:
      return '';
  }
}

function DevicesTable({
  rows,
  isColVis,
  onDelete,
  deletingId,
  onToggleRevoked,
  patchingId,
  collapsedGroups,
  onToggleGroup,
  duplicateNameSet,
  emptyLabel,
  publishedApp,
  onOpenInstallStatus,
  groupBy,
  attentionStrip,
  colFilters,
  colFilterOptions,
  onColFilterChange,
}) {
  const [openFilterKey, setOpenFilterKey] = useState('');
  const visibleColCount = DEVICE_COL_TOGGLE_KEYS.filter((k) => isColVis(k)).length + 1; /* + actions */

  const groups = useMemo(() => {
    const list = rows || [];
    if (groupBy === 'status') {
      const map = { ativos: [], revogados: [] };
      list.forEach((row) => {
        if (isRevoked(row)) map.revogados.push(row);
        else map.ativos.push(row);
      });
      return DEV_GROUPS.map((g) => ({ ...g, rows: map[g.key] })).filter((g) => g.rows.length);
    }
    if (groupBy === 'appVersion') {
      const map = new Map();
      list.forEach((row) => {
        const version = String(row.appVersion || '').trim();
        if (!map.has(version)) map.set(version, { version, rows: [] });
        map.get(version).rows.push(row);
      });
      return [...map.values()]
        .sort((a, b) => {
          if (!a.version) return 1;
          if (!b.version) return -1;
          return compareAppVersion(b.version, a.version);
        })
        .map((g) => {
          const upToDate = g.version ? isDeviceAppUpToDate({ appVersion: g.version }, publishedApp) : null;
          const n = g.rows.length;
          const status = upToDate === true ? ' · atualizada' : upToDate === false ? ' · desatualizada' : '';
          return {
            key: `v:${g.version || '__none__'}`,
            label: g.version ? `Versão ${g.version}` : 'Sem versão',
            desc: `${n} aparelho${n !== 1 ? 's' : ''}${status}`,
            color: upToDate === true ? '#22c55e' : upToDate === false ? '#f59e0b' : '#94a3b8',
            rows: g.rows,
          };
        });
    }
    return null;
  }, [rows, groupBy, publishedApp]);

  const activeCount = useMemo(() => (rows || []).filter((r) => !isRevoked(r)).length, [rows]);

  const renderFilterTh = (key, className) => (
    <GoDevThFilter
      key={key}
      colKey={key}
      label={DEV_COL_FILTER_LABELS[key]}
      className={className}
      options={colFilterOptions[key] || []}
      selected={colFilters[key] || []}
      onApply={(vals) => onColFilterChange(key, vals)}
      openKey={openFilterKey}
      setOpenKey={setOpenFilterKey}
    />
  );

  const renderDataRow = (row, idx) => {
    const rowKey = row.registrationId || row.id || `${row.deviceUid}-${idx}`;
    const deleting = deletingId === row.registrationId;
    const patching = patchingId === row.registrationId;
    const revoked = isRevoked(row);
    const undoLock = revoked ? getRevokeUndoLock(row.revogadoEm) : { locked: false };
    const revokeDisabled = patching || !row.registrationId || (revoked && undoLock.locked);
    const revokeTooltip = revoked
      ? formatRevokeLockTooltip(undoLock)
      : 'Revogar dispositivo';
    return (
      <tr key={rowKey} className="go-dev-row">
        {isColVis('nomeUsuario') ? (
          <td className="go-dev-td go-dev-td-user">
            <div className="go-dev-name-cell">
              <GoDevAvatar name={row.nomeUsuario} photoUrl={row.profilePhotoUrl} />
              {duplicateNameSet?.has(normalizeUserName(row.nomeUsuario)) ? (
                <GoHoverTooltip text="Usuário possui mais de um dispositivo registrado." align="start">
                  <span
                    className="go-dev-dup-dot"
                    aria-label="Usuário possui mais de um dispositivo registrado."
                  />
                </GoHoverTooltip>
              ) : null}
              <span className="go-dev-name-text">{row.nomeUsuario || '—'}</span>
            </div>
          </td>
        ) : null}
        {isColVis('install') ? (
          <td className="go-dev-td go-dev-td-install">
            {revoked ? (
              <span className="go-dev-install-muted">—</span>
            ) : (
              <GoDeviceInstallStatusChip
                deviceState={row.deviceState}
                onOpen={() => onOpenInstallStatus?.(row)}
              />
            )}
          </td>
        ) : null}
        {isColVis('brand') ? <td className="go-dev-td go-dev-td-brand">{row.brand || '—'}</td> : null}
        {isColVis('modelo') ? (
          <td className="go-dev-td go-dev-td-modelo">
            <GoDevEllipsisText value={row.modelo} />
          </td>
        ) : null}
        {isColVis('osVersion') ? (
          <td className="go-dev-td go-dev-td-os">
            <GoDevEllipsisText value={row.osVersion} />
          </td>
        ) : null}
        {isColVis('appVersion') ? (
          <td className="go-dev-td go-dev-td-app">
            {(() => {
              const upToDate = isDeviceAppUpToDate(row, publishedApp);
              const versionText = row.appVersion || '—';
              const statusIcon =
                upToDate === true ? (
                  <GoHoverTooltip text="App atualizado" align="center">
                    <span
                      className="go-dev-app-status go-dev-app-status--ok"
                      aria-label="App atualizado"
                    >
                      <CircleCheck size={14} strokeWidth={2.25} aria-hidden />
                    </span>
                  </GoHoverTooltip>
                ) : upToDate === false ? (
                  <GoHoverTooltip text="App desatualizado" align="center">
                    <span
                      className="go-dev-app-status go-dev-app-status--outdated"
                      aria-label="App desatualizado"
                    >
                      <TriangleAlert size={14} strokeWidth={2.25} aria-hidden />
                    </span>
                  </GoHoverTooltip>
                ) : null;
              const versionNode = row.appBuild ? (
                <GoHoverTooltip text={`Build ${row.appBuild}`} align="center">
                  <span className="go-dev-app-ver">{versionText}</span>
                </GoHoverTooltip>
              ) : (
                <span className="go-dev-app-ver">{versionText}</span>
              );
              return (
                <span className="go-dev-app-cell">
                  {versionNode}
                  {statusIcon}
                </span>
              );
            })()}
          </td>
        ) : null}
        {isColVis('deviceUid') ? <DeviceUidCell deviceUid={row.deviceUid} /> : null}
        {isColVis('ativadoEm') ? (
          <td className="go-dev-td go-dev-td-date">{formatDate(row.ativadoEm)}</td>
        ) : null}
        {isColVis('ultimoAcesso') ? (
          <td className="go-dev-td go-dev-td-datetime">
            <GoDevEllipsisText value={formatDateTime(row.ultimoAcesso)} />
          </td>
        ) : null}
        {isColVis('revogadoEm') ? (
          <td className="go-dev-td go-dev-td-revogado">
            <div className="go-dev-revogado-cell">
              {revoked ? (
                <GoHoverTooltip text={formatDate(row.revogadoEm)} align="center">
                  <span
                    className="go-dev-revogado-date-icon"
                    aria-label={`Revogado em ${formatDate(row.revogadoEm)}`}
                  >
                    <IconCalendarSearch size={16} stroke={1.75} aria-hidden />
                  </span>
                </GoHoverTooltip>
              ) : null}
              <GoDevToggle
                checked={revoked}
                disabled={revokeDisabled}
                ariaLabel={revoked ? 'Desfazer revogação' : 'Revogar dispositivo'}
                tooltip={revokeTooltip}
                onChange={(next) => onToggleRevoked(row, next)}
              />
            </div>
          </td>
        ) : null}
        <td className="go-dev-td go-dev-td-actions">
          <div className="go-dev-actions">
            <GoHoverTooltip text="Excluir dispositivo" align="end">
              <button
                type="button"
                className="go-dev-btn-delete"
                aria-label="Excluir dispositivo"
                disabled={deleting || !row.registrationId}
                onClick={() => onDelete(row)}
              >
                <Trash2 size={16} strokeWidth={1.75} aria-hidden />
              </button>
            </GoHoverTooltip>
          </div>
        </td>
      </tr>
    );
  };

  const hasColFilter = DEV_COL_FILTER_KEYS.some((k) => (colFilters[k] || []).length > 0);

  if (!rows.length && !hasColFilter) {
    return (
      <div className="go-dev-root">
        <div className="go-dev-panel">
          {attentionStrip}
          <div className="go-dev-foot-note">{emptyLabel || 'Nenhum dispositivo registrado.'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="go-dev-root">
      <div className="go-dev-panel">
        {attentionStrip}
        <div className="go-dev-table-scroll">
          <table className="go-dev-table">
            <thead>
              <tr>
                {isColVis('nomeUsuario') ? <th className="go-dev-th-user">Usuário</th> : null}
                {isColVis('install') ? renderFilterTh('install', 'go-dev-th-install') : null}
                {isColVis('brand') ? renderFilterTh('brand', 'go-dev-th-brand') : null}
                {isColVis('modelo') ? renderFilterTh('modelo', 'go-dev-th-modelo') : null}
                {isColVis('osVersion') ? renderFilterTh('osVersion', 'go-dev-th-os') : null}
                {isColVis('appVersion') ? renderFilterTh('appVersion', 'go-dev-th-app') : null}
                {isColVis('deviceUid') ? <th className="go-dev-th-uid">UID</th> : null}
                {isColVis('ativadoEm') ? <th className="go-dev-th-date">Ativação</th> : null}
                {isColVis('ultimoAcesso') ? <th className="go-dev-th-datetime">Últ. acesso</th> : null}
                {isColVis('revogadoEm') ? <th className="go-dev-th-revogado">Revogado</th> : null}
                <th className="go-dev-th-actions" aria-label="Ações" />
              </tr>
            </thead>
            <tbody>
              {!rows.length ? (
                <tr>
                  <td colSpan={visibleColCount} className="go-dev-td go-dev-td-empty">
                    Nenhum dispositivo para os filtros aplicados.
                  </td>
                </tr>
              ) : null}
              {!groups ? rows.map((row, idx) => renderDataRow(row, idx)) : null}
              {(groups || []).map((group) => {
                const groupRows = group.rows;
                const collapsed = !!collapsedGroups[group.key];
                return (
                  <React.Fragment key={group.key}>
                    <tr
                      className={`go-dev-group-row${collapsed ? ' collapsed' : ''}`}
                      onClick={() => onToggleGroup(group.key)}
                    >
                      <td colSpan={visibleColCount}>
                        <div className="go-dev-group-label">
                          <span className="go-dev-chev">
                            <ChevronDown size={13} strokeWidth={2.5} />
                          </span>
                          <span className="go-dev-dot" style={{ background: group.color }} />
                          {group.label}
                          <span className="go-dev-group-count">· {groupRows.length}</span>
                          <span className="go-dev-group-desc">{group.desc}</span>
                        </div>
                      </td>
                    </tr>
                    {!collapsed
                      ? groupRows.map((row, idx) => renderDataRow(row, idx))
                      : null}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="go-dev-foot-note">
          {rows.length} dispositivo{rows.length !== 1 ? 's' : ''}
          {' · '}
          {activeCount} ativo{activeCount !== 1 ? 's' : ''}
        </div>
      </div>
    </div>
  );
}

export default function GoDispositivosConfig() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [contractedDevices, setContractedDevices] = useState(0);
  const [usedDevices, setUsedDevices] = useState(0);
  const [revokeBlockedDevices, setRevokeBlockedDevices] = useState(0);
  const [percentUsed, setPercentUsed] = useState(0);
  const [idProduto, setIdProduto] = useState(null);
  const [publishedApp, setPublishedApp] = useState(null);
  const [devices, setDevices] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('ultimoAcesso');
  const [colVisibility, setColVisibility] = useState(fullColVisibility);
  const [deletingId, setDeletingId] = useState(null);
  const [patchingId, setPatchingId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [revokeDateYmd, setRevokeDateYmd] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [groupBy, setGroupBy] = useState('none');
  const [statusTab, setStatusTab] = useState('ativos');
  const [colFilters, setColFilters] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [installStatusDevice, setInstallStatusDevice] = useState(null);

  const isColVis = useCallback((key) => colVisibility[key] !== false, [colVisibility]);

  const loadData = useCallback(async (opts = {}) => {
    const silent = !!opts.silent;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_BASE_URL}/api/config/go/devices`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao carregar dispositivos');
      }
      const payload = data.data || {};
      setContractedDevices(Number(payload.contractedDevices) || 0);
      setUsedDevices(Number(payload.usedDevices) || 0);
      setRevokeBlockedDevices(Number(payload.revokeBlockedDevices) || 0);
      setPercentUsed(Number(payload.percentUsed) || 0);
      setIdProduto(payload.idProduto != null ? Number(payload.idProduto) : null);
      setPublishedApp(payload.publishedApp || null);
      setDevices(Array.isArray(payload.devices) ? payload.devices : []);
      if (!silent) setError(null);
    } catch (e) {
      if (!silent) {
        setError(e.message || 'Erro ao carregar');
        setDevices([]);
        setContractedDevices(0);
        setUsedDevices(0);
        setPercentUsed(0);
        setIdProduto(null);
        setPublishedApp(null);
      } else {
        throw e;
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const duplicateNameSet = useMemo(() => {
    const counts = new Map();
    devices.forEach((r) => {
      const key = normalizeUserName(r.nomeUsuario);
      if (!key) return;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    const set = new Set();
    counts.forEach((n, key) => {
      if (n > 1) set.add(key);
    });
    return set;
  }, [devices]);

  const statusCounts = useMemo(() => {
    const revogados = devices.filter(isRevoked).length;
    return {
      todos: devices.length,
      ativos: devices.length - revogados,
      revogados,
      duplicados: devices.filter((r) => duplicateNameSet.has(normalizeUserName(r.nomeUsuario))).length,
    };
  }, [devices, duplicateNameSet]);

  const attentionCounts = useMemo(() => {
    let outdated = 0;
    let installAttention = 0;
    devices.forEach((r) => {
      if (isRevoked(r)) return;
      if (isDeviceAppUpToDate(r, publishedApp) === false) outdated += 1;
      if (r.deviceState?.status === 'attention') installAttention += 1;
    });
    return { outdated, installAttention };
  }, [devices, publishedApp]);

  const statusRows = useMemo(() => {
    if (statusTab === 'ativos') return devices.filter((r) => !isRevoked(r));
    if (statusTab === 'revogados') return devices.filter(isRevoked);
    if (statusTab === 'duplicados') {
      return devices.filter((r) => duplicateNameSet.has(normalizeUserName(r.nomeUsuario)));
    }
    return devices;
  }, [devices, statusTab, duplicateNameSet]);

  const colFilterOptions = useMemo(() => {
    const out = {};
    DEV_COL_FILTER_KEYS.forEach((key) => {
      const set = new Set();
      statusRows.forEach((r) => set.add(colFilterValue(r, key)));
      out[key] = [...set].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true, sensitivity: 'base' }));
    });
    return out;
  }, [statusRows]);

  const filteredSortedRows = useMemo(() => {
    const q = String(searchQuery || '').trim().toLowerCase();
    let list = statusRows;
    if (q) {
      list = list.filter((r) => {
        const hay = `${r.nomeUsuario || ''} ${r.brand || ''} ${r.modelo || ''} ${r.osVersion || ''} ${r.appVersion || ''} ${r.appBuild || ''} ${r.deviceUid || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    DEV_COL_FILTER_KEYS.forEach((key) => {
      const sel = colFilters[key];
      if (sel?.length) {
        const set = new Set(sel);
        list = list.filter((r) => set.has(colFilterValue(r, key)));
      }
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = getSortValue(a, sortBy);
      const bv = getSortValue(b, sortBy);
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv), 'pt-BR', { numeric: true, sensitivity: 'base' });
      return cmp * dir;
    });
  }, [statusRows, searchQuery, sortBy, sortDir, colFilters]);

  const colsCustomized = DEVICE_COL_TOGGLE_KEYS.some((k) => !isColVis(k));

  const handleExportXlsx = useCallback(() => {
    const headers = [
      ...DEVICE_COL_KEYS.filter((k) => isColVis(k)).map((k) => DEVICE_COL_LABELS[k]),
      'Instalação',
    ];
    const installLabel = (row) => {
      if (isRevoked(row)) return '—';
      const st = row.deviceState?.status;
      if (st === 'ok') return 'OK';
      if (st === 'attention') return 'Atenção';
      return 'Sem relatório';
    };
    const matrix = [
      headers,
      ...filteredSortedRows.map((row) => [
        ...DEVICE_COL_KEYS.filter((k) => isColVis(k)).map((key) => {
          if (key === 'ativadoEm' || key === 'revogadoEm') return formatDate(row[key]);
          if (key === 'ultimoAcesso') return formatDateTime(row[key]);
          return row[key] || '';
        }),
        installLabel(row),
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(matrix);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Dispositivos');
    const stamp = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `go-dispositivos-${stamp}.xlsx`);
  }, [filteredSortedRows, isColVis]);

  const handleDeleteDevice = useCallback((row) => {
    const regId = row?.registrationId;
    if (!regId) return;
    setDeleteConfirm({ row });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const row = deleteConfirm?.row;
    const regId = row?.registrationId;
    setDeleteConfirm(null);
    if (!regId) return;

    setDeletingId(regId);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${API_BASE_URL}/api/config/go/devices/${encodeURIComponent(regId)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Falha ao excluir dispositivo');
      }
      await loadData({ silent: true });
    } catch (e) {
      window.alert(e.message || 'Erro ao excluir dispositivo');
    } finally {
      setDeletingId(null);
    }
  }, [deleteConfirm, loadData]);

  const applyRevokePatch = useCallback(
    async (row, revoked, revogadoEm) => {
      const regId = row?.registrationId;
      if (!regId) return;
      setPatchingId(regId);
      try {
        const token = localStorage.getItem('authToken');
        const body = { revoked: !!revoked };
        if (revoked && revogadoEm) body.revogadoEm = revogadoEm;
        const res = await fetch(
          `${API_BASE_URL}/api/config/go/devices/${encodeURIComponent(regId)}`,
          {
            method: 'PATCH',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
          }
        );
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) {
          throw new Error(data.message || 'Falha ao atualizar revogação');
        }
        await loadData({ silent: true });
      } catch (e) {
        window.alert(e.message || 'Erro ao atualizar revogação');
      } finally {
        setPatchingId(null);
      }
    },
    [loadData]
  );

  const handleToggleRevoked = useCallback(
    (row, revoked) => {
      const regId = row?.registrationId;
      if (!regId) return;

      if (revoked) {
        setRevokeDateYmd(minRevokeYmd());
        setRevokeConfirm({ row });
        return;
      }

      const lock = getRevokeUndoLock(row.revogadoEm);
      if (lock.locked) {
        window.alert(formatRevokeLockTooltip(lock));
        return;
      }
      applyRevokePatch(row, false);
    },
    [applyRevokePatch]
  );

  const handleConfirmRevoke = useCallback(() => {
    const row = revokeConfirm?.row;
    const ymd = String(revokeDateYmd || '').trim();
    if (!ymd || ymd < minRevokeYmd()) return;
    setRevokeConfirm(null);
    if (row) applyRevokePatch(row, true, ymd);
  }, [revokeConfirm, revokeDateYmd, applyRevokePatch]);

  const handleToggleGroup = useCallback((key) => {
    setCollapsedGroups((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  return (
    <div className="pcs2-page go-dispositivos-page">
      {revokeConfirm ? (
        <GoDevConfirmModal
          title="Revogar dispositivo"
          message={`Revogar "${revokeConfirm.row?.nomeUsuario || revokeConfirm.row?.deviceUid || 'este dispositivo'}"? Escolha a data a partir da qual a revogação poderá ser desfeita.`}
          confirmLabel="Revogar"
          modalClassName="go-dev-confirm-modal--revoke"
          onCancel={() => setRevokeConfirm(null)}
          onConfirm={handleConfirmRevoke}
        >
          <div className="go-dev-confirm-cal">
            <p className="go-dev-confirm-date-label">
              {revokeDateYmd ? formatPickerDate(revokeDateYmd) : 'Selecione a data'}
            </p>
            <RangeCalendarSingle
              minDate={minRevokeYmd()}
              maxDate={maxRevokeYmd()}
              startDate={revokeDateYmd || null}
              endDate={revokeDateYmd || null}
              dayMode
              initialMonthDate={revokeDateYmd || minRevokeYmd()}
              onDateSelect={(dateStr) => {
                if (dateStr && dateStr >= minRevokeYmd()) setRevokeDateYmd(dateStr);
              }}
            />
          </div>
        </GoDevConfirmModal>
      ) : null}
      {deleteConfirm ? (
        <GoDevConfirmModal
          title="Excluir dispositivo"
          message="Atenção: se você excluir este dispositivo, ele só poderá ser adicionado novamente após 30 dias."
          confirmLabel="Excluir"
          onCancel={() => setDeleteConfirm(null)}
          onConfirm={handleConfirmDelete}
        />
      ) : null}
      <GoDeviceInstallStatusModal
        open={!!installStatusDevice}
        device={installStatusDevice}
        onClose={() => setInstallStatusDevice(null)}
      />
      <Pcs2Section
        className="go-dispositivos-section"
        icon={IconCategoryPlus}
        tablerIcon
        title="Licenciamento"
        desc="Gestão de licenças e dispositivos em uso"
      >
        <Pcs2Subblock
          title="Licenças"
          desc={`${usedDevices.toLocaleString('pt-BR')} dispositivo${usedDevices !== 1 ? 's' : ''} em uso de ${contractedDevices.toLocaleString('pt-BR')} contratado${contractedDevices !== 1 ? 's' : ''}.`}
        >
          <div className="go-dev-license-row">
            <LicenseUsagePanel
              used={usedDevices}
              max={contractedDevices}
              percent={percentUsed}
              idProduto={idProduto}
              loading={loading}
              error={error && !devices.length ? error : null}
            />
            <GoAppDownloadPanel publishedApp={publishedApp} loading={loading} />
          </div>
        </Pcs2Subblock>
      </Pcs2Section>

      <hr className="pcs2-divider" />

      <Pcs2Section
        className="go-dispositivos-section go-dispositivos-section-table"
        icon={Smartphone}
        title="Dispositivos"
        desc="Lista de aparelhos registrados no App Go."
        asideMiddle={
          <GoDevStatusTabs value={statusTab} onChange={setStatusTab} counts={statusCounts} />
        }
        asideAction={
          <GoDevicesToolbar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            displayMenuProps={{
              sortBy,
              sortDir,
              onSortChange: setSortBy,
              onSortDirChange: setSortDir,
              groupBy,
              onGroupByChange: setGroupBy,
              isColVis,
              onToggleCol: (k) => {
                setColVisibility((prev) => ({ ...prev, [k]: prev[k] === false ? true : false }));
              },
              onRestoreCols: () => setColVisibility(fullColVisibility()),
              colsCustomized,
              onExport: handleExportXlsx,
              exportDisabled: loading || filteredSortedRows.length === 0,
              exportCount: filteredSortedRows.length,
              onRefresh: () => loadData(),
              refreshDisabled: loading,
              refreshBusy: loading,
            }}
          />
        }
      >
        <Pcs2Subblock>
          {loading ? (
            <ProcessConfigSkeleton variant="tableBody" ariaLabel="Carregando dispositivos" />
          ) : error && !devices.length ? (
            <div className="go-dev-error">
              <span>{error}</span>
              <button type="button" className="go-dev-retry" onClick={loadData}>
                Tentar novamente
              </button>
            </div>
          ) : (
            <DevicesTable
              rows={filteredSortedRows}
              isColVis={isColVis}
              onDelete={handleDeleteDevice}
              deletingId={deletingId}
              onToggleRevoked={handleToggleRevoked}
              patchingId={patchingId}
              collapsedGroups={collapsedGroups}
              onToggleGroup={handleToggleGroup}
              duplicateNameSet={duplicateNameSet}
              emptyLabel={
                statusTab === 'duplicados'
                  ? 'Nenhum usuário duplicado.'
                  : statusTab === 'revogados'
                    ? 'Nenhum dispositivo revogado.'
                    : 'Nenhum dispositivo registrado.'
              }
              publishedApp={publishedApp}
              onOpenInstallStatus={setInstallStatusDevice}
              groupBy={groupBy}
              attentionStrip={
                <GoDevAttentionStrip
                  outdatedCount={attentionCounts.outdated}
                  installAttentionCount={attentionCounts.installAttention}
                  revokeBlockedCount={revokeBlockedDevices}
                />
              }
              colFilters={colFilters}
              colFilterOptions={colFilterOptions}
              onColFilterChange={(key, vals) => setColFilters((prev) => ({ ...prev, [key]: vals }))}
            />
          )}
        </Pcs2Subblock>
      </Pcs2Section>
    </div>
  );
}
